import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  TextField,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  Collapse,
  Button,
  Chip,
  Tooltip,
  IconButton
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  ColorLens as ColorLensIcon
} from "@mui/icons-material";
import { convertDateToWords } from "../utils/dateToWords";

const toCamelCase = (str) => {
  if (!str) return "";
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .split(' ')
    .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(' ');
};

// Student Info Fields Configuration - Single Column
const STUDENT_INFO_FIELDS = {
  photo: { label: "Photo", defaultVisible: true },
  rollNo: { label: "Roll No", defaultVisible: true },
  name: { label: "Student's Name", defaultVisible: true },
  father: { label: "Father's Name", defaultVisible: true },
  mother: { label: "Mother's Name", defaultVisible: true },
  apaarId: { label: "Apaar ID", defaultVisible: true },
  aadhaarNumber: { label: "Aadhaar Number", defaultVisible: false },
  studied: { label: "Studied (Class)", defaultVisible: true },
  admissionNo: { label: "Admission No.", defaultVisible: true },
  standard: { label: "Standard", defaultVisible: false },
  // caste: { label: "Caste", defaultVisible: false },
  casteCategory: { label: "Category/Caste", defaultVisible: false },
  dob: { label: "Date of Birth", defaultVisible: true },
  dobInWords: { label: "DOB (In Words)", defaultVisible: false },
  heightWeight: { label: "Height/Weight", defaultVisible: false },
  // gender: { label: "Gender", defaultVisible: false },
  // bloodGroup: { label: "Blood Group", defaultVisible: false },
  contact: { label: "Contact Number", defaultVisible: false },
  // email: { label: "Email", defaultVisible: false },
  address: { label: "Address", defaultVisible: false },

  // fullname: { label: "Student's full Name", defaultVisible: true, column: 1 },
};


const SinglePageMarksheetReceipt = ({ receiptData, school, setTemID }) => {
  const student = receiptData.data || [];
  const receiptId = `marksheet`;

  // --- Customization controls ---
  const [themeColor, setThemeColor] = useState("#1976d2");
  const [showSettings, setShowSettings] = useState(false);
  const [fieldVisibility, setFieldVisibility] = useState(() => {
    const saved = localStorage.getItem('singlePageMarksheetFieldVisibility');
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.entries(STUDENT_INFO_FIELDS).forEach(([key, field]) => {
      defaults[key] = field.defaultVisible;
    });
    return defaults;
  });

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Save visibility settings
  useEffect(() => {
    localStorage.setItem('singlePageMarksheetFieldVisibility', JSON.stringify(fieldVisibility));
  }, [fieldVisibility]);

  // extract unique subjects
  const subjects = useMemo(() => {
    const allMarks = student.flatMap((ms) => ms.marks || []);
    const unique = [...new Map(allMarks.map((m) => [m.subjectName?.trim(), m])).values()];
    return unique.sort((a, b) =>
      a.subjectName?.toLowerCase().localeCompare(b.subjectName?.toLowerCase())
    );
  }, [student]);

  // quick lookup
  const findMark = (examType, subject) => {
    const term = student.find((m) => m.examType?.trim().toLowerCase()
      .includes(examType?.trim().toLowerCase()));
    return term?.marks?.find(
      (s) => s.subjectName?.trim().toLowerCase() === subject?.trim().toLowerCase()
    );
  };

  const getTotals = (examType) => {
    const term = student.find((m) => m.examType?.trim().toLowerCase()
      .includes(examType?.trim().toLowerCase()));
    if (!term || !term.marks) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks.reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks.reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade || "-" };
  };

  const t1 = getTotals("First");
  const t2 = getTotals("Second");
  const t3 = getTotals("Final");

  // Toggle field visibility
  const toggleFieldVisibility = (fieldKey) => {
    setFieldVisibility(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Toggle all fields
  const toggleAllFields = (visible) => {
    const newState = {};
    Object.keys(STUDENT_INFO_FIELDS).forEach(key => {
      newState[key] = visible;
    });
    setFieldVisibility(newState);
  };

  // Get visibility count
  const visibleCount = Object.values(fieldVisibility).filter(v => v).length;
  const totalFields = Object.keys(STUDENT_INFO_FIELDS).length;

  // Get student data
  const getStudentData = () => {
    const data = student.find((m) => m.examType?.trim().toLowerCase().includes('first'));
    return data;
  };

  const studentData = getStudentData();

  // Render student info field
  const renderStudentField = (fieldKey) => {
    if (!fieldVisibility[fieldKey]) return null;

    const field = STUDENT_INFO_FIELDS[fieldKey];
    let value = '';

    switch (fieldKey) {
      case 'apaarId':
        value = studentData?.apaarId || '';
        break;
      case 'name':
        value = toCamelCase(`${studentData?.name || ''} ${studentData?.last || ''}`.trim());
        break;
      case 'father':
        value = toCamelCase(`${studentData?.father || ''} ${studentData?.last || ''}`.trim());
        break;
      case 'mother':
        value = toCamelCase(`${studentData?.mother || ''} ${studentData?.last || ''}`.trim());
        break;
      case 'studied':
        value = `${studentData?.classRoom?.standard || ''} | ${studentData?.classRoom?.section || ''}`;
        break;
      case 'admissionNo':
        value = studentData?.id || '';
        break;
      case 'casteCategory':
        value = `${studentData?.category} | caste: ${studentData?.caste || ''}`;
        break;
      case 'dob':
        value = studentData?.birthday ? new Date(studentData.birthday).toLocaleDateString("en-GB") : '';
        break;
      case 'dobInWords':
        value = studentData?.birthday ? convertDateToWords(studentData.birthday) : '';
        break;
      case 'heightWeight':
        return (
          <Box key={fieldKey} display={'flex'} justifyContent={'space-between'} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>{field.label}</Typography>
            <TextField
              variant="standard"
              placeholder="Height/Weight"
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Box>
        );
      case 'photo':
        return studentData?.photo ? (
          <Box key={fieldKey} display="flex" justifyContent="center" my={1}>
            <img
              src={studentData.photo}
              alt="Student"
              style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover' }}
              onError={(e) => e.target.style.display = 'none'}
            />
          </Box>
        ) : null;
      case 'aadhaarNumber':
        value = studentData?.aadhaar || '';
        break;
      case 'contact':
        value = studentData?.contact || studentData?.mobile || '';
        break;
      case 'standard':
        value = `${studentData?.classRoom?.standard || ''} | ${studentData?.classRoom?.section || ''}`;
        break;
      case 'address':
        value = toCamelCase([studentData?.address, studentData?.address?.city, studentData?.address?.pincode, studentData?.address?.state].filter(Boolean).join(", ")) ||
          '';
        break;
      case 'rollNo':
        value = studentData?.rollNumber || studentData?.id || '';
        break;
      default:
        return null;
    }

    if (!value || value === '') return null;

    return (
      <Box
        key={fieldKey}
        sx={{
          display: "grid",
          gridTemplateColumns: "180px auto",
          mb: 0.5,
          gap: 1,
          alignItems: "center"
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>{field.label} :</Typography>
        <Typography sx={{ fontSize: "0.9rem" }}>{value}</Typography>
      </Box>
    );
  };

  // Get visible fields in order
  const visibleFields = Object.keys(STUDENT_INFO_FIELDS)
    .filter(key => fieldVisibility[key]);

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);

  return (
    <Box id={receiptId}>
      {/* Controls Panel */}
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-evenly",
          maxWidth: "800px",
          margin: "auto",
          mb: 2,
          p: 2,
          "@media print": { display: "none" },
        }}
        elevation={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Theme Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 40,
              height: 32,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setShowSettings(!showSettings)}
          >
            Customize
          </Button>
          <Tooltip title={`${visibleCount}/${totalFields} fields visible`}>
            <Chip
              size="small"
              label={`${visibleCount}/${totalFields}`}
              color="primary"
              variant="outlined"
            />
          </Tooltip>
        </Box>
      </Paper>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper
          sx={{
            maxWidth: "800px",
            margin: "auto",
            mb: 2,
            p: 2,
            bgcolor: '#f8f9fa',
            "@media print": { display: "none" },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight="600">
              Toggle Student Info Fields
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => toggleAllFields(true)}>
                Show All
              </Button>
              <Button size="small" onClick={() => toggleAllFields(false)}>
                Hide All
              </Button>
            </Box>
          </Box>
          <Grid container spacing={1}>
            {Object.entries(STUDENT_INFO_FIELDS).map(([key, field]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldVisibility[key] || false}
                      onChange={() => toggleFieldVisibility(key)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption">{field.label}</Typography>
                      {fieldVisibility[key] ? (
                        <VisibilityIcon fontSize="small" color="primary" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="disabled" />
                      )}
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Collapse>

      {/* Page 1 - Student Profile */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 2,
        p: 3,
        background: "#fff",
        fontFamily: "Arial",
        maxWidth: "800px",
        margin: "auto",
        border: `2px solid ${borderColor}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
      }}>
        <Box sx={{ p: 1, width: "100%" }}>
          {/* Header */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
            <img
              src={school?.logoUrl}
              alt="School Logo"
              style={{ width: 100, height: 90, marginTop: 10 }}
              onError={(e) => e.target.style.display = 'none'}
            />

            <Typography variant="h4" align="center" fontWeight="bold" sx={{ color: borderColor }}>
              {toCamelCase(school?.schoolName)}
            </Typography>
            <Typography variant="subtitle1" align="center">
              {toCamelCase([school?.addressDTO?.addressLine1, school?.addressDTO?.city, school?.addressDTO?.pincode, school?.addressDTO?.state].filter(Boolean).join(", "))}
            </Typography>
            <Typography variant="subtitle1" align="center" fontWeight="bold">
              PROGRESS REPORT SESSION - {student[0]?.academicYear}
            </Typography>

            {/* Photo - shown if visible */}
            {fieldVisibility.photo && studentData?.photo && (
              <img
                src={studentData.photo}
                alt="Student"
                style={{ width: 120, height: 120, marginTop: 10, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${borderColor}` }}
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </Box>

          <Typography variant="subtitle1" align="center" fontWeight="bold" sx={{ bgcolor: headerBg, py: 0.5, borderRadius: 1 }}>
            {"STUDENT PROFILE"}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Student Info - Single Column Layout */}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            maxWidth: "600px",
            margin: "0 auto",
            px: 2
          }}>
            {/* Photo handled separately above */}
            {visibleFields
              .filter(key => key !== 'photo')
              .map(key => renderStudentField(key))}
          </Box>
        </Box>
      </Box>

      <div className="pagebreak"></div>

      {/* Page 2 - Marks Table */}
      <Box sx={{
        p: 3,
        background: "#fff",
        fontFamily: "Arial",
        maxWidth: "800px",
        margin: "auto",
        border: `2px solid ${borderColor}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
      }}
        style={{
          backgroundImage: `url(${school?.logoUrl})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundColor: "rgb(255 255 255 / 87%)",
          backgroundBlendMode: "lighten"
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "column", alignItems: "center", mb: 1, mt: 0 }}>
          <Typography variant="h6" align="center" fontWeight="bold" sx={{ color: borderColor }}>
            {toCamelCase(school?.schoolName)}
          </Typography>
          <Typography variant="subtitle1" align="center">
            {toCamelCase([school?.addressLine2, school?.city, school?.postalCode, school?.state].filter(Boolean).join(", "))}
          </Typography>
          <Typography align="center" sx={{ fontWeight: "bold", my: 1 }}>
            PROGRESS REPORT SESSION - {student[0]?.academicYear}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2, mt: 1, borderColor }} />

        {/* Student Info on second page */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography><strong>Student's Name: </strong>
            {toCamelCase(`${[student[0]?.name, student[0]?.father, student[0]?.last].filter(Boolean).join(" ")}`)}
          </Typography>
          <Typography><strong>Standard: </strong>{student[0]?.classRoom?.standard} | {student[0]?.classRoom?.section}</Typography>
          {/* <Typography><strong>Roll No.: </strong> {student[0]?.rollNumber}</Typography> */}
        </Box>
        <Divider sx={{ m: 1, borderColor }} />

        {/* Marks Table */}
        <Box sx={{ py: 2, mb: 3, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px", background: headerBg }} rowSpan={2} align="center">
                  <strong>Subject</strong>
                </TableCell>

                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px", background: headerBg }} colSpan={4} align="center">
                  <strong>Term-I</strong>
                </TableCell>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px", background: headerBg }} colSpan={4} align="center">
                  <strong>Term-II</strong>
                </TableCell>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px", background: headerBg }} colSpan={4} align="center">
                  <strong>Final Exam</strong>
                </TableCell>
              </TableRow>

              <TableRow>
                {[...Array(3)].flatMap(() => [
                  <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px", background: headerBg }} align="center">Max</TableCell>,
                  <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px", background: headerBg }} align="center">Min</TableCell>,
                  <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px", background: headerBg }} align="center">Obt</TableCell>,
                  <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px", background: headerBg }} align="center">Grade</TableCell>,
                ])}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((sub, i) => {
                const term1 = findMark("First", sub.subjectName);
                const term2 = findMark("Second", sub.subjectName);
                const finalExam = findMark("Final", sub.subjectName);

                return (
                  <TableRow key={i}>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }}>{toCamelCase(sub.subjectName)}</TableCell>

                    {/* Term-I */}
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{term1?.maxMark || 100}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{Math.round((term1?.maxMark || 100) * 34 / 100)}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{term1?.marksObtained || "-"}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{term1?.grade || "-"}</TableCell>

                    {/* Term-II */}
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{term2?.maxMark || 100}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{Math.round((term2?.maxMark || 100) * 34 / 100)}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{term2?.marksObtained || "-"}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{term2?.grade || "-"}</TableCell>

                    {/* Final Exam */}
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{finalExam?.maxMark || 100}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{Math.round((finalExam?.maxMark || 100) * 34 / 100)}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{finalExam?.marksObtained || "-"}</TableCell>
                    <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{finalExam?.grade || "-"}</TableCell>
                  </TableRow>
                );
              })}

              {/* Totals Row */}
              <TableRow sx={{ background: alpha(borderColor, 0.06) }}>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }}><strong>Total Marks</strong></TableCell>

                {/* Term-I */}
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t1.max}</TableCell>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} />
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t1.obt}</TableCell>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t1.grade}</TableCell>

                {/* Term-II */}
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t2.max}</TableCell>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} />
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t2.obt}</TableCell>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t2.grade}</TableCell>

                {/* Final Exam */}
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t3.max}</TableCell>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} />
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t3.obt}</TableCell>
                <TableCell sx={{ border: `1px solid ${borderColor}`, padding: "8px" }} align="center">{t3.grade}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Result Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="body2"><strong>Result:</strong> {student[2]?.result || ''}</Typography>
          <Typography variant="body2" color="textSecondary">
            <b>Remarks: </b>{student[2]?.remarks || ''}
          </Typography>
          <Typography variant="body2"><strong>Percentage:</strong> {student[2]?.percentage || ''}%</Typography>
        </Box>

        {/* Footer */}
        <Box my={8} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Typography variant="body2">Parent's Signature: _______</Typography>
          <Typography variant="body2">Class Teacher Sign.: _______</Typography>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Principal/HeadMaster
            </Typography>
            <Typography variant="body2">Seal/Sign.: ________</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SinglePageMarksheetReceipt;